import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useMemo } from "react";
import { GenericConfigContext } from "/contexts/generic-config-context";
import { getCurrencyName, getCurrencyWithPriceDisplay, getCurrencySign, shouldShowCurrencyText } from "/services/pricing-service";
import { getFreeTierSeatsCountByAbTest } from "services/ab-tests/monetization-ab-tests-service";
import { productPrices } from "services/pricing/product-prices";
import { TIERS } from "constants/pricing";
import { handleRuntimeError } from "client/services/exceptions-handling-service/exceptions-handling-service";
import { StyledProductPrice } from "./product-price.scss";
export const ProductPrice = ({ period, seats, tierName, product, hideFreeForever, splitCurrencySign, isNewPricing })=>{
    const { translate, translateOrTranslateWithParam, currency: currencyFromContext, pricingVersion, localeId, forceCurrency, cloudfrontViewerCountry, cookies, forcePricing, abTests } = useContext(GenericConfigContext);
    const currency = currencyFromContext || getCurrencyName({
        forceCurrency,
        countryCode: cloudfrontViewerCountry
    });
    const productPricePerMonth = productPrices[product][pricingVersion][period][tierName][currency];
    const corePricePerMonth = useMemo(()=>productPrices.core[pricingVersion][period][tierName][currency], [
        period,
        tierName,
        pricingVersion,
        currency
    ]);
    const totalPricePerMonth = product === "core" ? productPricePerMonth : productPricePerMonth + corePricePerMonth;
    if (totalPricePerMonth === undefined) {
        const params = {
            product,
            period,
            currency,
            tierName,
            seats
        };
        handleRuntimeError(`ProductPrice: error calculating total price per month. Params: ${JSON.stringify(params)}`);
    }
    const pricePerSeat = getCurrencyWithPriceDisplay({
        price: totalPricePerMonth.toLocaleString(),
        currency,
        localeId,
        hideCurrency: splitCurrencySign
    });
    const totalPrice = getCurrencyWithPriceDisplay({
        price: (totalPricePerMonth * seats).toLocaleString(),
        currency,
        localeId,
        className: "product-price-total"
    });
    const currencySignDisplay = useMemo(()=>getCurrencySign(currency), [
        currency
    ]);
    const renderUpToSeats = ()=>{
        return /*#__PURE__*/ _jsx("div", {
            className: "product-price-total",
            children: translateOrTranslateWithParam({
                key: "pricingPage.upToXSeats",
                param: getFreeTierSeatsCountByAbTest({
                    cookies,
                    abTests,
                    forcePricing
                })
            })
        });
    };
    const renderPricePerMonth = ()=>{
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "product-price-total-wrapper",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: "product-price-total",
                            children: translate("pricingPage.total")
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: "product-price-total",
                            children: totalPrice
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: "product-price-total",
                            children: translate("pricingPage.perMonthSlash")
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "product-price-billed-periodically",
                    children: translate(`pricingPage.periods.${period}.billed`)
                })
            ]
        });
    };
    const renderFreeForever = ()=>{
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                translate("pricingPage.free"),
                /*#__PURE__*/ _jsx("br", {}),
                " ",
                translate("pricingPage.forever")
            ]
        });
    };
    const renderSeatPerMonth = ()=>{
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                translate("pricingPage.seatOver"),
                /*#__PURE__*/ _jsx("br", {}),
                " ",
                translate("pricingPage.month")
            ]
        });
    };
    const renderDiscountChip = ()=>{
        return /*#__PURE__*/ _jsx("div", {
            className: "discount-chip",
            children: translateOrTranslateWithParam({
                key: "pricingPage.percentageOff",
                param: 18
            })
        });
    };
    const renderNewPriceDescription = ()=>{
        return /*#__PURE__*/ _jsxs("div", {
            className: "price-description-new",
            children: [
                translate("pricingPage.perSeatPerMonth"),
                ", ",
                period === "monthly" ? translate("pricingPage.billedMonthly") : translate("pricingPage.billedAnnually")
            ]
        });
    };
    const shouldSeeFreeForever = useMemo(()=>{
        return tierName === TIERS.FREE && !hideFreeForever;
    }, [
        hideFreeForever,
        tierName
    ]);
    return /*#__PURE__*/ _jsxs(StyledProductPrice, {
        className: "price",
        isNewPricing: isNewPricing,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "price-tag",
                children: [
                    splitCurrencySign && /*#__PURE__*/ _jsxs("div", {
                        className: `currency-sign ${isNewPricing ? 'bold' : ''}`,
                        children: [
                            currencySignDisplay,
                            shouldShowCurrencyText(currency) && /*#__PURE__*/ _jsx("div", {
                                className: "price-currency-text",
                                children: currency.toUpperCase()
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "amount",
                        children: pricePerSeat
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "price-description",
                        children: [
                            shouldSeeFreeForever && renderFreeForever(),
                            !shouldSeeFreeForever && !isNewPricing && renderSeatPerMonth(),
                            isNewPricing && period === "yearly" && renderDiscountChip()
                        ]
                    })
                ]
            }),
            tierName === TIERS.FREE && !isNewPricing && renderUpToSeats(),
            tierName !== TIERS.FREE && !isNewPricing && renderPricePerMonth(),
            isNewPricing && renderNewPriceDescription(),
            /*#__PURE__*/ _jsx("div", {})
        ]
    });
};
